import { AnimationEvent, animate, animation, style, transition, trigger, useAnimation } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DomHandler } from 'primeng/dom';
import { UniqueComponentId, ZIndexUtils } from 'primeng/utils';

import { Confirmation } from '@core/interfaces';
import { ConfirmationService } from '@core/services';

import { CONSTANTS } from './constants';

const showAnimation = animation([
  style({ transform: '{{transform}}', opacity: 0 }),
  animate('{{transition}}', style({ transform: 'none', opacity: 1 })),
]);

const hideAnimation = animation([animate('{{transition}}', style({ transform: '{{transform}}', opacity: 0 }))]);

@Component({
  selector: 'z-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  animations: [
    trigger('animation', [
      transition('void => visible', [useAnimation(showAnimation)]),
      transition('visible => void', [useAnimation(hideAnimation)]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<boolean>();

  @Input() title: string;
  @Input() message: string;
  @Input() icon: string;

  @Input() style: any;
  @Input() styleClass: string;
  @Input() maskStyleClass: string;

  @Input() acceptIcon: string = 'pi pi-check';
  @Input() acceptLabel: string;
  @Input() acceptVisible: boolean = true;
  @Input() acceptButtonStyleClass: string;

  @Input() rejectIcon: string = 'pi pi-times';
  @Input() rejectLabel: string;
  @Input() rejectVisible: boolean = true;
  @Input() rejectButtonStyleClass: string;

  @Input() cancelIcon: string = 'pi pi-times';
  @Input() cancelLabel: string = 'No';
  @Input() cancelVisible: boolean = false;
  @Input() cancelButtonStyleClass: string;

  @Input() defaultFocus: string = 'accept';
  @Input() closeOnEscape: boolean = true;
  @Input() dismissableMask: boolean;
  @Input() blockScroll: boolean = true;
  @Input() closable: boolean = true;

  @Input() appendTo: any;
  @Input() autoZIndex: boolean = true;
  @Input() baseZIndex: number = 0;
  @Input() transitionOptions: string = '150ms cubic-bezier(0, 0, 0.2, 1)';
  @Input() focusTrap: boolean = true;

  @Input() get visible(): boolean {
    return this._visible;
  }
  set visible(value: boolean) {
    this.confirmationService.visible = value;
    this._visible = value;

    if (this._visible && !this.maskVisible) {
      this.maskVisible = true;
    }

    this.changeDetectorRef.markForCheck();
  }

  @Input() get position(): string {
    return this._position;
  }

  set position(value: string) {
    this._position = value;

    switch (value) {
      case 'top-left':
      case 'bottom-left':
      case 'left':
        this.transformOptions = 'translate3d(-100%, 0px, 0px)';
        break;
      case 'top-right':
      case 'bottom-right':
      case 'right':
        this.transformOptions = 'translate3d(100%, 0px, 0px)';
        break;
      case 'bottom':
        this.transformOptions = 'translate3d(0px, 100%, 0px)';
        break;
      case 'top':
        this.transformOptions = 'translate3d(0px, -100%, 0px)';
        break;
      default:
        this.transformOptions = 'scale(0.7)';
        break;
    }
  }

  @ViewChild('content') contentViewChild: ElementRef<HTMLDivElement>;

  closeAction: 'cancel' | 'reject' | 'accept';

  confirmation: Confirmation;

  _visible: boolean;

  maskVisible: boolean;

  documentEscapeListener: any;

  container: HTMLDivElement;

  wrapper: HTMLElement;

  contentContainer: HTMLDivElement;

  maskClickListener: () => void;

  preWidth: number;

  _position: string = 'center';

  transformOptions: any = 'scale(0.7)';

  id = UniqueComponentId();

  acceptEvent: EventEmitter<any>;
  rejectEvent: EventEmitter<any>;
  cancelEvent: EventEmitter<any>;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private confirmationService: ConfirmationService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.confirmationService.source$.pipe(takeUntil(this.destroy$)).subscribe((options) => {
      this.message = options.message || this.message;
      this.icon = options.icon || this.icon;
      this.title = options.title || this.title;
      this.rejectVisible = options.rejectVisible == null ? this.rejectVisible : options.rejectVisible;
      this.acceptVisible = options.acceptVisible == null ? this.acceptVisible : options.acceptVisible;
      this.acceptLabel = options.acceptLabel || this.acceptLabel;
      this.rejectLabel = options.rejectLabel || this.rejectLabel;
      this.acceptIcon = options.acceptIcon || this.acceptIcon;
      this.rejectIcon = options.rejectIcon || this.rejectIcon;
      this.acceptButtonStyleClass = options.acceptButtonStyleClass || this.acceptButtonStyleClass;
      this.rejectButtonStyleClass = options.rejectButtonStyleClass || this.rejectButtonStyleClass;
      this.defaultFocus = options.defaultFocus || this.defaultFocus;

      this.blockScroll = options.blockScroll === false || options.blockScroll === true ? options.blockScroll : this.blockScroll;

      this.closeOnEscape = options.closeOnEscape === false || options.closeOnEscape === true ? options.closeOnEscape : this.closeOnEscape;

      this.dismissableMask =
        options.dismissableMask === false || options.dismissableMask === true ? options.dismissableMask : this.dismissableMask;

      this.visible = true;

      this.confirmationService.pending = false;

      this.confirmation = {
        title: this.title,
        message: this.message,
        icon: this.icon,
        accept: options.accept ?? null,
        acceptLabel: this.acceptLabel,
        acceptIcon: this.acceptIcon,
        acceptVisible: this.acceptVisible,
        acceptButtonStyleClass: this.acceptButtonStyleClass,
        reject: options.reject ?? null,
        rejectLabel: this.rejectLabel,
        rejectIcon: this.rejectIcon,
        rejectVisible: this.rejectVisible,
        rejectButtonStyleClass: this.rejectButtonStyleClass,
        cancel: options.cancel ?? null,
        cancelLabel: this.cancelLabel,
        cancelIcon: this.cancelIcon,
        cancelVisible: this.cancelVisible,
        cancelButtonStyleClass: this.cancelButtonStyleClass,
        blockScroll: this.blockScroll,
        closeOnEscape: this.closeOnEscape,
        dismissableMask: this.dismissableMask,
        defaultFocus: this.defaultFocus,
        acceptEvent: this.acceptEvent,
        rejectEvent: this.rejectEvent,
        cancelEvent: this.cancelEvent,
      };

      if (this.confirmation.accept) {
        this.acceptEvent = new EventEmitter();
        this.acceptEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
          if (typeof this.confirmation.accept === 'function') {
            this.confirmation.accept();
          }
        });
      }

      if (this.confirmation.reject) {
        this.rejectEvent = new EventEmitter();
        this.rejectEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
          if (typeof this.confirmation.reject === 'function') {
            this.confirmation.reject();
          }
        });
      }

      if (this.confirmation.cancel) {
        this.cancelEvent = new EventEmitter();
        this.cancelEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
          if (typeof this.confirmation.cancel === 'function') {
            this.confirmation.cancel();
          }
        });
      }

      this.closeAction = options.closeAction || 'reject';

      this.confirmationService.confirmOpen$.next(this.confirmation);
    });
  }

  onAnimationStart(event: AnimationEvent) {
    let element: any;

    switch (event.toState) {
      case 'visible':
        this.container = event.element;
        this.wrapper = this.container.parentElement;
        this.contentContainer = DomHandler.findSingle(this.container, '.confirm-dialog-content');
        this.container.setAttribute(this.id, '');
        this.appendContainer();
        this.moveOnTop();
        this.bindGlobalListeners();
        this.enableModality();

        element = this.getElementToFocus();

        if (element) {
          element.focus();
        }

        break;
    }
  }

  onAnimationEnd(event: AnimationEvent) {
    switch (event.toState) {
      case 'void':
        this.onOverlayHide();
        break;
    }
  }

  getElementToFocus() {
    switch (this.defaultFocus) {
      case 'accept':
        return DomHandler.findSingle(this.container, '.confirm-dialog-accept');

      case 'reject':
        return DomHandler.findSingle(this.container, '.confirm-dialog-reject');

      case 'close':
        return DomHandler.findSingle(this.container, '.dialog-header-close');

      case 'none':
        return null;

      //backward compatibility
      default:
        return DomHandler.findSingle(this.container, '.confirm-dialog-accept');
    }
  }

  appendContainer() {
    if (this.appendTo) {
      if (this.appendTo === 'body') document.body.appendChild(this.wrapper);
      else DomHandler.appendChild(this.wrapper, this.appendTo);
    }
  }

  restoreAppend() {
    if (this.wrapper && this.appendTo) {
      this.elementRef.nativeElement.appendChild(this.wrapper);
    }
  }

  enableModality() {
    if (this.blockScroll) {
      DomHandler.addClass(document.body, 'p-overflow-hidden');
    }

    if (this.dismissableMask) {
      this.maskClickListener = this.renderer.listen(this.wrapper, 'mousedown', (event: any) => {
        if (this.wrapper && this.wrapper.isSameNode(event.target)) {
          this.close(event);
        }
      });
    }
  }

  disableModality() {
    this.maskVisible = false;

    if (this.blockScroll) {
      DomHandler.removeClass(document.body, 'p-overflow-hidden');
    }

    if (this.dismissableMask) {
      this.unbindMaskClickListener();
    }

    if (this.container && !this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
    }
  }

  moveOnTop() {
    if (this.autoZIndex) {
      ZIndexUtils.set('modal', this.container, this.baseZIndex + 1000);
      this.wrapper.style.zIndex = String(parseInt(this.container.style.zIndex, 10) - 1);
    }
  }

  getMaskClass() {
    const maskClass = { 'confirm-dialog-mask p-component-overlay': true, 'p-dialog-mask-scrollblocker': this.blockScroll };
    maskClass[this.getPositionClass().toString()] = true;
    return maskClass;
  }

  getPositionClass() {
    const positions = ['left', 'right', 'top', 'top-left', 'top-right', 'bottom', 'bottom-left', 'bottom-right'];
    const pos = positions.find((item) => item === this.position);

    return pos ? `confirm-dialog-${pos}` : '';
  }

  bindGlobalListeners() {
    if ((this.closeOnEscape && this.closable) || (this.focusTrap && !this.documentEscapeListener)) {
      const documentTarget: any = this.elementRef ? this.elementRef.nativeElement.ownerDocument : 'document';

      this.documentEscapeListener = this.renderer.listen(documentTarget, 'keydown', (event) => {
        if (event.which == 27 && this.closeOnEscape && this.closable) {
          if (parseInt(this.container.style.zIndex) === ZIndexUtils.get(this.container) && this.visible) {
            this.close(event);
          }
        }

        if (event.which === 9 && this.focusTrap) {
          event.preventDefault();

          const focusableElements = DomHandler.getFocusableElements(this.container);

          if (focusableElements && focusableElements.length > 0) {
            if (!focusableElements[0].ownerDocument.activeElement) {
              focusableElements[0].focus();
            } else {
              const focusedIndex = focusableElements.indexOf(focusableElements[0].ownerDocument.activeElement);

              if (event.shiftKey) {
                if (focusedIndex == -1 || focusedIndex === 0) {
                  focusableElements[focusableElements.length - 1].focus();
                } else {
                  focusableElements[focusedIndex - 1].focus();
                }
              } else {
                if (focusedIndex == -1 || focusedIndex === focusableElements.length - 1) {
                  focusableElements[0].focus();
                } else {
                  focusableElements[focusedIndex + 1].focus();
                }
              }
            }
          }
        }
      });
    }
  }

  unbindGlobalListeners() {
    if (this.documentEscapeListener) {
      this.documentEscapeListener();
      this.documentEscapeListener = null;
    }
  }

  unbindMaskClickListener() {
    if (this.maskClickListener) {
      this.maskClickListener();
      this.maskClickListener = null;
    }
  }

  onOverlayHide() {
    if (this.container && this.autoZIndex) {
      ZIndexUtils.clear(this.container);
    }

    this.disableModality();
    this.unbindGlobalListeners();
    this.container = null;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    this.restoreAppend();
    this.onOverlayHide();
  }

  acceptClick() {
    if (this.acceptEvent) {
      this.acceptEvent.emit();
    }

    this.hide(CONSTANTS.ACCEPT);
  }

  rejectClick() {
    if (this.rejectEvent) {
      this.rejectEvent.emit();
    }

    this.hide(CONSTANTS.REJECT);
  }

  cancelClick() {
    if (this.cancelEvent) {
      this.cancelEvent.emit();
    }

    this.hide(CONSTANTS.CANCEL);
  }

  close(event: Event) {
    event.preventDefault();

    if (this.closeAction === 'accept' && this.acceptEvent) {
      this.acceptEvent.emit();
      this.hide(CONSTANTS.ACCEPT);
    } else if (this.closeAction === 'reject' && this.rejectEvent) {
      this.rejectEvent.emit();
      this.hide(CONSTANTS.CANCEL);
    } else if (this.closeAction === 'cancel' && this.cancelEvent) {
      this.cancelEvent.emit();
      this.hide(CONSTANTS.CANCEL);
    }
  }

  hide(type: string) {
    this.visible = false;
    this.confirmation.result = type;
    this.confirmationService.confirmClose$.next(this.confirmation);
  }
}
